import { CloseIcon, TimeIcon, CheckIcon } from '@chakra-ui/icons'
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Modal,
  ModalBody,
  Alert,
  AlertDescription,
  AlertIcon,
  Icon,
  Spinner,
  Button,
  ModalFooter,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { Row } from './SendScript.styled'
import { isValidPrecriberNumberFormat } from '../../../utils/validation-helpers/prescriberNumberValidate'
import { useAppDispatch, useAppSelector } from '../../../app/typedReduxHooks'
import GoogleAnalyticsTagManager from '../../../utils/google-analytics/GoogleAnalyticsTagManager'
import { useGAContextData } from '../../../utils/google-analytics/useGAContextData'
import { Patient, PrescribedItem, Prescriber } from '../../../types'
import { isValidEntityID } from '../../../utils/validation-helpers/PrescriberValidate'
import { definedPrescribedItemsSelector } from '../ScriptForm/PrescribedItem/PrescribedItemSlice'
import { useSSOContext } from '../../../features/sso'
import useHIValidate from '../../../hooks/useHIValidate'
import useApi from '../../../common/utils/api/useApi'
import { createScids } from '../ScriptForm/PrescribedItem/services'
import { useValidateScriptNo } from '../../../hooks/useValidateScriptNo'
import { useValidateDrugs } from '../../../hooks/useValidateDrugs'
import { v4 as uuidv4 } from 'uuid'
import { setHasSentLowTokenNotification } from '../../Billing/BillingSlice'
import { StepValidationStatus } from '../../../types/hi-validation'
import { usePrescribingLocation } from '../../../features/locations/hooks/usePrescribingLocation'
import { AnalyticsProperties, trackEvent } from '../../../common/utils/analytics'

const INIT_ERX_ERROR = 'Could not connect with eRx service, please try again'

const validatePrescriber = (prescriber: Prescriber) => {
  if (!isValidPrecriberNumberFormat(prescriber?.prescriberNumber)) {
    return 'Prescriber number is missing or invalid'
  }
  if (!prescriber?.familyName) {
    return 'Prescriber family name is missing or invalid'
  }
  if (!prescriber?.hpii) {
    return 'Prescriber HPII number is missing or invalid'
  }
  if (!prescriber?.qualifications) {
    return 'Prescriber qualifications field is missing'
  }

  if (!isValidEntityID(prescriber?.entityId)) {
    return 'Prescriber eRx entity ID is missing or invalid'
  }
  return ''
}

const validatePatient = (selectedPatient: Patient) => {
  if (!selectedPatient?.address_1) {
    return 'Please make sure your patient has a valid address'
  }

  if (!selectedPatient?.given_names) {
    return 'Patient first name is missing or invalid'
  }
  if (!selectedPatient?.family_name) {
    return 'Patient family name is missing or invalid'
  }
  if (!selectedPatient?.gender) {
    return 'Patient gender is missing or invalid'
  }
  if (!selectedPatient?.date_of_birth) {
    return 'Patient date of birth is missing or invalid'
  }
  if (!selectedPatient?.ihi_number) {
    return 'Patient IHI number is missing or invalid'
  }
  return ''
}

type SendScriptStep = {
  label: string
  isLoading: boolean
  isValid: StepValidationStatus
  error: unknown
}

export type ReauthenticationInfo = {
  isAuthModalOpen: boolean
  isSSOAuthModalOpen: boolean
  isDone: boolean
  ssoError?: string
}

type SendScriptOverallCheckModalProps = {
  isOpen: boolean
  onClose: () => void
  error?: string
  onSubmitPrescriptions?: (
    scids: string[],
    guids: string[],
    prescribedItems?: PrescribedItem[]
  ) => Promise<void>
  reauthenticationInfo: ReauthenticationInfo
  setReauthenticationInfo: (reauthenticationInfo: ReauthenticationInfo) => void
}

function SendScriptOverallCheckModal({
  isOpen,
  onClose,
  onSubmitPrescriptions,
  reauthenticationInfo,
  setReauthenticationInfo,
}: SendScriptOverallCheckModalProps) {
  const prescriber = useAppSelector((state) => state.prescriber.prescriber)
  const selectedPatient = useAppSelector((state) => state.patientDetail.selectedPatient)
  const { prescribingLocation } = usePrescribingLocation()
  const drugs = useAppSelector(definedPrescribedItemsSelector)
  const prescribedItems = useAppSelector((state) => state.newScript.prescribedItemList)

  const [overallError, setOverallError] = useState<string>('')
  const [isScidValid, setIsScidValid] = useState<StepValidationStatus>('NOT_START')
  const GAContextData = useGAContextData()
  const {
    validateHI: validateHIHook,
    hpiiValidation,
    hpioValidation,
    ihiValidation,
  } = useHIValidate()
  const dispatch = useAppDispatch()
  const { isSSOUser: shouldUseSSOAuthentication } = useSSOContext()

  const { validateDrugs } = useValidateDrugs({
    reauthenticationInfo,
    onRequireReauthentication: () => {
      if (shouldUseSSOAuthentication) {
        setReauthenticationInfo({ ...reauthenticationInfo, isSSOAuthModalOpen: true })
      } else {
        setReauthenticationInfo({ ...reauthenticationInfo, isAuthModalOpen: true })
      }
    },
  })

  const handleValidationError = (error: string | null | Error) => {
    if (!error) return false

    const errorMessage = error instanceof Error ? error.message : error
    setOverallError(errorMessage)
    GoogleAnalyticsTagManager.sendPrescriptionCreationErrorEvent({
      ...GAContextData,
      error: errorMessage,
    })
    return true
  }

  const { validateScriptNumber, isGeneratingScriptNo, isScriptNoValid, scriptNoGenerationError } =
    useValidateScriptNo({
      drugs: prescribedItems,
      onError: (errorMessage) => {
        handleValidationError(errorMessage)
      },
    })

  const {
    trigger: generateScidNumber,
    loading: generateScidLoading,
    error: generateScidError,
  } = useApi(createScids, {
    onSuccess: (scids: string[]) => {
      if (scids.length === drugs.length) {
        setIsScidValid('VALID')
        return
      }
      setIsScidValid('INVALID')
    },
    onFailure: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate SCID'
      setOverallError(errorMessage)
      GoogleAnalyticsTagManager.sendPrescriptionCreationErrorEvent({
        ...GAContextData,
        error: errorMessage,
      })
    },
  })

  // Run validation checks once when modal opens
  // Note: Intentionally using empty deps to run only on mount
  useEffect(() => {
    startOverallCheck()
  }, [])

  const steps = [
    {
      label: 'Validating HPII...',
      isLoading: hpiiValidation.isLoading,
      isValid: hpiiValidation.status,
      error: hpiiValidation.error,
    },
    {
      label: 'Validating HPIO...',
      isLoading: hpioValidation.isLoading,
      isValid: hpioValidation.status,
      error: hpioValidation.error,
    },
    {
      label: 'Validating IHI...',
      isLoading: ihiValidation.isLoading,
      isValid: ihiValidation.status,
      error: ihiValidation.error,
    },
    {
      label: 'Initiating the prescription...',
      isLoading: generateScidLoading || isGeneratingScriptNo,
      isValid: isScidValid === 'VALID' ? isScriptNoValid : isScidValid,
      error: generateScidError || scriptNoGenerationError ? INIT_ERX_ERROR : null,
    },
  ]

  const numbersCheckStatusIcon = (step: Omit<SendScriptStep, 'label'>) => {
    if (step.isLoading) {
      return <Spinner />
    }
    if (step.isValid === 'INVALID' || !!step.error) {
      return <Icon as={CloseIcon} color="red.500" w={6} h={6} />
    }
    if (step.isValid === 'NOT_START') {
      return <Icon as={TimeIcon} color="blue.500" w={6} h={6} />
    }
    if (step.isValid === 'VALID' && !step.error) {
      return <Icon as={CheckIcon} color="green.500" w={6} h={6} />
    }
  }

  const validateHI = async () => {
    const result = await validateHIHook()
    return result.hpii && result.hpio && result.ihi
  }

  const generateGuid = () => {
    return Array.from({ length: drugs.length }, () => uuidv4())
  }

  const stoppedAt = (stage: AnalyticsProperties<'script_send_failed'>['stage']) =>
    trackEvent('script_send_failed', { stage, itemCount: drugs.length })

  const startOverallCheck = async () => {
    if (!prescriber || !prescribingLocation || !selectedPatient || !drugs.length) {
      stoppedAt('missing_data')
      handleValidationError(
        'Please make sure you have provide all the data required(Prescriber, Clinic, Patient, Drugs)'
      )
      return
    }

    const prescriberError = validatePrescriber(prescriber)
    const patientError = validatePatient(selectedPatient)
    if (prescriberError || patientError) stoppedAt('prescriber_patient_check')
    if (handleValidationError(prescriberError)) return
    if (handleValidationError(patientError)) return

    const drugsValidationResult = validateDrugs()
    if (!drugsValidationResult.isValid) {
      stoppedAt('drug_check')
      handleValidationError(drugsValidationResult.error)
      return
    }

    const hiCheckResult = await validateHI()
    if (!hiCheckResult) {
      stoppedAt('hi_check')
      GoogleAnalyticsTagManager.sendPrescriptionCreationErrorEvent({
        ...GAContextData,
        error: 'Failed to valid hpii or hpio or ihi',
      })
      return
    }

    const scids = await generateScidNumber(drugs.length)
    if (!scids) {
      stoppedAt('scid_generation')
      handleValidationError('Failed to generate scid number')
      return
    }

    const latestPrescribedItems = await validateScriptNumber()
    if (!latestPrescribedItems) {
      stoppedAt('script_number')
      handleValidationError('Failed to generate script number')
      return
    }

    const latestDefinedPrescribedItems = latestPrescribedItems.filter(
      (item): item is PrescribedItem => item !== undefined
    )

    const guids = generateGuid()
    dispatch(setHasSentLowTokenNotification(false))

    if (onSubmitPrescriptions && scids.length === drugs.length && guids.length === drugs.length) {
      setTimeout(() => {
        handleClose()
        onSubmitPrescriptions(scids, guids, latestDefinedPrescribedItems)
      }, 100)
    }
  }

  const handleClose = () => {
    setOverallError('')
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="3xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Creating Prescription...</ModalHeader>
        <ModalBody>
          {overallError && (
            <Alert status="error">
              <AlertIcon />
              <AlertDescription fontSize="sm">{overallError}</AlertDescription>
            </Alert>
          )}
          {steps.map((step) => {
            return (
              <Row key={step.label}>
                {numbersCheckStatusIcon(step)}
                <span>{step.label}</span>
              </Row>
            )
          })}
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClose} disabled={steps.some((step) => step.isLoading)}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default SendScriptOverallCheckModal
